import { useQueries, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { TASK_KIND, TASK_STATUS, formatDateOnly } from "@brainlog/shared";
import { ApiError, errorMessage, get, patch } from "@/lib/api";
import { qk } from "@/lib/queries";
import type { Member, Task } from "@/lib/types";
import { openNote, openPaper, openView } from "@/lib/store";
import { cn } from "@/lib/utils";

export interface TaskLink {
  targetType: string;
  targetId: string;
  title?: string | null;
  accessible?: boolean;
  noteId?: string;
  manuscriptId?: string;
  kind?: string;
  status?: string;
}

/** Task as returned by GET /api/tasks (enriched by the server). */
export type TaskRow = Omit<Task, "links"> & {
  sourceKind?: string | null;
  sourceManuscriptId?: string | null;
  links?: TaskLink[];
};

export const STATUS_ORDER = TASK_STATUS.values;

/** Whole-day difference between two date-only strings (no timezone conversion involved). */
export function daysBetween(from: string, to: string) {
  const [y1, m1, d1] = from.split("-").map(Number);
  const [y2, m2, d2] = to.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
}

export function isOverdue(t: Pick<TaskRow, "status" | "dueDate">, today: string) {
  return !!t.dueDate && t.status !== "done" && t.dueDate < today;
}

export function dueText(dueDate: string | null, today: string, status?: string) {
  if (!dueDate) return "No due date";
  const diff = daysBetween(today, dueDate);
  const date = formatDateOnly(dueDate);
  if (status === "done") return `Due ${date}`;
  if (diff < 0) return `Overdue — due ${date}`;
  if (diff === 0) return `Due today (${date})`;
  if (diff === 1) return `Due tomorrow (${date})`;
  return `Due ${date} (in ${diff} days)`;
}

export function statusLabel(s: string) {
  return (TASK_STATUS.labels as Record<string, string>)[s] ?? s;
}

export function kindLabel(k: string) {
  return (TASK_KIND.labels as Record<string, string>)[k] ?? k;
}

export function KindBadge({ kind }: { kind: string }) {
  if (kind === "task") return null;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-1.5 py-0 text-[0.7rem] font-medium uppercase tracking-wide",
        kind === "milestone" && "border-primary/40 text-primary",
        kind === "deliverable" && "border-warning/50 text-warning",
        kind === "revision" && "border-ai/50 text-ai",
      )}
    >
      {kindLabel(kind)}
    </span>
  );
}

/** Members for every workspace that appears in a task list (cached per workspace). */
export function useMembersByWorkspace(workspaceIds: string[]) {
  const results = useQueries({
    queries: workspaceIds.map((id) => ({
      queryKey: qk.workspace(id),
      queryFn: () => get<{ workspace: unknown; members: Member[] }>(`/api/workspaces/${id}`),
      staleTime: 60_000,
    })),
  });
  const map: Record<string, Member[]> = {};
  workspaceIds.forEach((id, i) => {
    const d = results[i]?.data as { members: Member[] } | undefined;
    if (d) map[id] = d.members;
  });
  return map;
}

/** PATCH a task with optimistic-concurrency version; reports conflicts and server warnings. */
export function useTaskUpdater() {
  const qc = useQueryClient();
  return async function updateTask(task: Pick<TaskRow, "id" | "version" | "title">, body: Record<string, unknown>) {
    try {
      const r = await patch<{ task: TaskRow; warnings?: string[] }>(`/api/tasks/${task.id}`, { ...body, version: task.version });
      for (const w of r.warnings ?? []) toast.warning(w);
      return r.task;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast.error(`“${task.title}” was changed by someone else. The latest version has been loaded; please check and try again.`);
      } else toast.error(errorMessage(e));
      return null;
    } finally {
      await qc.invalidateQueries({ queryKey: ["tasks"] });
      await qc.invalidateQueries({ queryKey: ["overview"] });
    }
  };
}

export function openTaskSource(t: TaskRow) {
  if (t.sourceType === "note" && t.sourceId && t.sourceAccessible) openNote(t.sourceId, t.sourceTitle ?? "Source note", t.workspaceId);
  else if (t.sourceType === "revision_item" && t.sourceAccessible && t.sourceManuscriptId) openView("manuscript", t.workspaceId, "Manuscript", undefined, t.sourceManuscriptId);
}

export function openTaskLink(l: TaskLink, workspaceId: string) {
  if (l.accessible === false) return;
  if (l.targetType === "note") openNote(l.targetId, l.title ?? "Note", workspaceId);
  else if (l.targetType === "manuscript_section" && l.noteId) openNote(l.noteId, l.title ?? "Section", workspaceId);
  else if (l.targetType === "manuscript") openView("manuscript", workspaceId, l.title ?? "Manuscript", undefined, l.targetId);
  else if (l.targetType === "paper") openPaper(l.targetId, l.title ?? "Paper", undefined, workspaceId);
}

export const LINK_TYPE_LABEL: Record<string, string> = {
  note: "Record",
  manuscript_section: "Section",
  manuscript: "Manuscript",
  paper: "Paper",
};

/** Native select styled like the rest of the app (accessible, keyboard-friendly). */
export function NativeSelect(props: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string; hideLabel?: boolean }) {
  const { label, hideLabel, className, id, ...rest } = props;
  return (
    <label className={cn("inline-flex min-w-0 items-center gap-1 text-xs text-muted-foreground", className)} htmlFor={id}>
      <span className={cn(hideLabel && "sr-only")}>{label}</span>
      <select id={id} {...rest} className="h-7 min-w-0 rounded-md border bg-background px-1.5 text-xs text-foreground" />
    </label>
  );
}

export function readLocal<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
}

export function writeLocal(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}
